import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { noop } from '@utils';
import Button, { ButtonEnums } from '../Button';
import HotkeysPreferences from '../HotkeysPreferences';
import { HotkeyDefinition } from '../HotkeysPreferences/HotkeysPreferences';
import Select from '../Select';
import Typography from '../Typography';

interface UserPreferencesProps {
	availableLanguages: Array<{ label: string; value: string }>;
	currentLanguage: string;
	defaultLanguage: string;
	disabled?: boolean;
	hotkeyDefaults: Record<string, HotkeyDefinition>;
	hotkeyDefinitions: Record<string, HotkeyDefinition>;
	onCancel?: (..._args) => void;
	onSubmit?: (..._args) => void;
	onReset?: (..._args) => void;
	hotkeysModule: {
		initialize: () => void;
		pause: () => void;
		unpause: () => void;
		startRecording: () => void;
		record: (id: string) => void;
	};
}

interface SectionProps {
	title: string;
	children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
	<>
		<div className="mb-2 border-b-2 border-black">
			<Typography variant="inherit" color="primaryLight" className="flex pb-2 text-[16px] font-semibold !leading-[1.2]">
				{title}
			</Typography>
		</div>
		<div className="mb-8 mt-4">{children}</div>
	</>
);

const UserPreferences = ({
	// languageOptions = [
	// 	{
	// 		value: 'ONE',
	// 		label: 'ONE'
	// 	},
	// 	{
	// 		value: 'TWO',
	// 		label: 'TWO'
	// 	}
	// ],
	availableLanguages,
	defaultLanguage,
	currentLanguage,
	disabled = false,
	hotkeyDefinitions,
	hotkeyDefaults,
	onCancel = noop,
	onSubmit = noop,
	onReset = noop,
	hotkeysModule,
}: UserPreferencesProps) => {
	const { t } = useTranslation('UserPreferencesModal');
	const [state, setState] = useState({
		isDisabled: disabled,
		hotkeyErrors: {},
		hotkeyDefinitions,
		language: currentLanguage,
	});

	const onSubmitHandler = () => {
		onSubmit(state);
	};

	const onResetHandler = () => {
		setState((state) => ({
			...state,
			language: defaultLanguage,
			hotkeyDefinitions: hotkeyDefaults,
			hotkeyErrors: {},
			isDisabled: disabled,
		}));
		onReset();
	};

	const onCancelHandler = () => {
		setState({
			...state,
			hotkeyDefinitions,
		});
		onCancel();
	};

	const onLanguageChangeHandler = (value) => {
		setState((state) => ({
			...state,
			language: value,
		}));
	};

	const onHotkeysChangeHandler = (id, definition, errors) => {
		setState((state) => ({
			...state,
			isDisabled: Object.values(errors).every((e) => e !== undefined),
			hotkeyErrors: errors,
			hotkeyDefinitions: {
				...state.hotkeyDefinitions,
				[id]: definition,
			},
		}));
	};

	return (
		<>
			<Section title={t('General')}>
				<div className="flex w-72 flex-row items-center justify-center">
					<Typography variant="subtitle" className="mr-5 h-full text-right">
						{t('Language')}
					</Typography>
					<Select
						id="select-language"
						isClearable={false}
						onChange={onLanguageChangeHandler}
						options={availableLanguages}
						value={state.language}
					/>
				</div>
			</Section>
			<Section title={t('Hotkeys')}>
				<HotkeysPreferences
					disabled={disabled}
					hotkeyDefinitions={state.hotkeyDefinitions}
					onChange={onHotkeysChangeHandler}
					errors={state.hotkeyErrors}
					hotkeysModule={hotkeysModule}
				/>
			</Section>
			<div className="flex flex-row justify-between">
				<Button type={ButtonEnums.type.secondary} onClick={onResetHandler} disabled={disabled}>
					{t('Reset to Defaults')}
				</Button>
				<div className="flex flex-row">
					<Button type={ButtonEnums.type.secondary} onClick={onCancelHandler}>
						{t('Cancel')}
					</Button>
					<Button disabled={state.isDisabled} className="ml-2" onClick={onSubmitHandler}>
						{t('Save')}
					</Button>
				</div>
			</div>
		</>
	);
};

export default UserPreferences;
